import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Widget, WidgetDeviceView, WidgetPosition, WidgetState } from '../../campaign/widgets.model';
import { WidgetService } from '../services/widget.service';
import { Logger, LoggerFactory } from '../../core/logger.service';
import WidgetComponentHelpers from '../../campaign/widget-component-helpers';
import { LanguageService } from '../../core/language.service';
import { OnsiteCampaignType } from '../../campaign/campaign.model';
import { Subscription } from 'rxjs';
import { ThemeService } from '../services/theme.service';
import { PageTitle } from '../utils/page-title';
import { CurrentContextService } from '../../core/current-context.service';

@Component({
  selector: 'app-widget-preview-dialog',
  templateUrl: './widget-preview-dialog.component.html',
  styleUrls: ['./widget-preview-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WidgetPreviewDialogComponent implements OnInit, OnDestroy {
  private readonly logger: Logger;
  public loadingWidget = false;
  public widgetId: number;
  public imageUrls: Array<{ imageUrl: string; position: WidgetPosition }>;
  public widgetLink: string;
  public isDarkTheme: boolean;
  public isMobilePreview: boolean;
  private isDarkThemeSub: Subscription;
  private subscriptionArray: Subscription[] = [];
  public campaignId: number;
  public currentPageTitle: string;
  public PageTitle = PageTitle;

  constructor(
    public readonly loggerFactory: LoggerFactory,
    private languageService: LanguageService,
    public dialogRef: MatDialogRef<WidgetPreviewDialogComponent>,
    private readonly widgetService: WidgetService,
    private readonly themeService: ThemeService,
    private readonly currentContextService: CurrentContextService,
    @Inject(MAT_DIALOG_DATA) public data: { widgetId: number; campaignId: number; currentPageTitle: string },
  ) {
    this.logger = loggerFactory.get('WidgetPreviewDialogComponent');
    this.widgetId = data.widgetId;
    this.campaignId = data.campaignId;
    this.currentPageTitle = data.currentPageTitle;
  }

  ngOnInit() {
    if (this.widgetId) this.getWidgetsById(this.widgetId);
    else if (this.campaignId) this.getWidgetsByCampaign(this.campaignId);
    this.isDarkThemeSub = this.themeService.isDarkThemeBS.subscribe((item) => {
      this.isDarkTheme = item;
    });
    this.subscriptionArray.push(
      this.currentContextService.currentContextBS.subscribe((context) => {
        if (context) {
          this.isMobilePreview = context.customer?.defaultEditorView === WidgetDeviceView.Mobile;
        }
      }),
    );
  }

  public getWidgetsById(widgetId: number) {
    this.loadingWidget = true;
    this.widgetService.getWidgetById(widgetId).subscribe(
      (widget: Widget) => {
        this.logger.debug('Loaded widget: ' + JSON.stringify(widget));
        this.loadingWidget = false;
        if (widget.campaign.type === OnsiteCampaignType.ABTesting) {
          this.widgetLink = `/campaigns/ab-tests/${widget.campaign.id}/settings`;
        } else {
          this.widgetLink = `/campaigns/${widget.campaign.id}/widgets/${widget.id}/editor`;
        }
        this.parseWidgetAndGetImages(widget);
      },
      (error) => {
        this.widgetLink = undefined;
        this.loadingWidget = false;
        this.logger.error('Error that should break the page, widgets could not be retrieved: ' + JSON.stringify(error));
        this.closeDialog();
      },
    );
  }

  public getWidgetsByCampaign(campaignId: number) {
    this.loadingWidget = true;
    this.widgetService.getWidgetsByCampaignId(campaignId).subscribe(
      (widgets: any[]) => {
        if (widgets.length) {
          this.logger.debug('Loaded widgets: ' + JSON.stringify(campaignId));
          this.loadingWidget = false;
          if (widgets[0].campaign.type === OnsiteCampaignType.ABTesting) {
            this.widgetLink = `/campaigns/ab-tests/${campaignId}/settings`;
          } else {
            this.widgetLink = `/campaigns/${campaignId}/settings`;
          }
          this.parseWidgetAndGetImages(widgets[0]);
        }
      },
      (error) => {
        this.widgetLink = undefined;
        this.loadingWidget = false;
        this.logger.error('Error that should break the page, widgets could not be retrieved: ' + JSON.stringify(error));
        this.closeDialog();
      },
    );
  }

  private parseWidgetAndGetImages(widget: Widget) {
    const statesSplit = WidgetComponentHelpers.splitAndNameWidgetStatesByType(widget.states, this.languageService);
    const imageArray = [];
    if (statesSplit.regulars && statesSplit.regulars.length > 0) {
      statesSplit.regulars.forEach((state: WidgetState) =>
        imageArray.push({
          imageUrl: state.previewImageUrl,
          position: state.position,
        }),
      );
    }
    if (statesSplit.success) {
      imageArray.push({
        imageUrl: statesSplit.success.previewImageUrl,
        position: statesSplit.success.position,
      });
    }
    if (statesSplit.teaser) {
      imageArray.push({
        imageUrl: statesSplit.teaser.previewImageUrl,
        position: statesSplit.teaser.position,
      });
    }
    this.imageUrls = imageArray;
  }

  private closeDialog() {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.subscriptionArray.push(this.isDarkThemeSub);
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }
}
